import React, { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { AdminCreateMembershipModal } from '@/components/AdminCreateMembershipModal';
import { supabase } from '@/lib/supabase';
import { useStax } from '@/hooks/useStax';
import {
  AdminMembershipStatus,
  AdminMembershipSummary,
  AvailableSlot,
  cancelMembership,
  bookVisit,
  payoffMembership,
  stopAutoRenew,
  fetchAdminMembershipDetail,
  fetchAdminMemberships,
  fetchAvailableSlots,
  updateMembershipStatus,
  updatePaymentMethodAdmin,
} from '@/lib/memberships-api-client';

const STATUS_OPTIONS = [
  { label: 'All statuses', value: '' },
  { label: 'Active', value: 'active' },
  { label: 'Pending payment', value: 'pending_payment' },
  { label: 'Past due', value: 'past_due' },
  { label: 'Suspended', value: 'suspended' },
  { label: 'Expired', value: 'expired' },
  { label: 'Cancelled', value: 'cancelled' },
];

const MEMBERSHIP_COMPANY_ALLOWLIST = [
  { id: 'abraham-ac', brandKey: 'abraham', displayName: 'Abraham AC' },
  { id: 'climate', brandKey: 'climate', displayName: 'Climate Control Services' },
  { id: 'hales-ac', brandKey: 'hales', displayName: 'Hales AC' },
] as const;

function formatCents(amount?: number) {
  if (amount === undefined || amount === null) return '—';
  const cents = Number(amount);
  if (!Number.isFinite(cents)) return '—';
  const sign = cents < 0 ? '-' : '';
  return `${sign}$${(Math.abs(cents) / 100).toFixed(2)}`;
}

function formatDate(date?: string) {
  if (!date) return '—';
  const d = new Date(date);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

export default function MembershipManagementPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<AdminMembershipStatus | ''>('');
  const [companyId, setCompanyId] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedSlotKey, setSelectedSlotKey] = useState('');
  const [visitType, setVisitType] = useState<'pm' | 'repair' | 'emergency'>('pm');
  const [visitNotes, setVisitNotes] = useState('');
  const [cardMonth, setCardMonth] = useState('');
  const [cardYear, setCardYear] = useState('');
  const [statusUpdate, setStatusUpdate] = useState<AdminMembershipStatus | ''>('');
  const [statusReason, setStatusReason] = useState('');
  const [payoffReason, setPayoffReason] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const queryClient = useQueryClient();
  const paymentFormRef = useRef<HTMLFormElement>(null);
  const { isStaxReady, cardFormStatus, staxError, setBrandKey, tokenizePaymentMethod } = useStax();

  const { data: companies } = useQuery({
    queryKey: ['admin-companies'],
    queryFn: async () => {
      const { data, error } = await supabase.from('companies').select('id, name, brand_key').order('name');
      if (error) throw error;
      const rows = (data || []) as any[];
      const chosenByBrandKey = new Map<string, any>();

      for (const { id, brandKey, displayName } of MEMBERSHIP_COMPANY_ALLOWLIST) {
        const match = rows.find((c) => c.id === id) || rows.find((c) => c.brand_key === brandKey);
        if (!match) continue;

        chosenByBrandKey.set(brandKey, {
          ...match,
          brand_key: match.brand_key || brandKey,
          name: displayName,
        });
      }

      return Array.from(chosenByBrandKey.values()).sort((a, b) =>
        String(a.name || '').localeCompare(String(b.name || ''))
      );
    },
    staleTime: 5 * 60_000,
  });

  const companyById = useMemo(() => {
    return new Map((companies || []).map((c: any) => [c.id, c]));
  }, [companies]);

  const refreshData = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-memberships'] });
    if (selectedId) {
      queryClient.invalidateQueries({ queryKey: ['admin-membership', selectedId] });
      queryClient.invalidateQueries({ queryKey: ['admin-available-slots', selectedId] });
    }
  };

  const { data: memberships, isLoading, isFetching, error } = useQuery({
    queryKey: ['admin-memberships', search, status, companyId],
    queryFn: () => fetchAdminMemberships({ search, status, company_id: companyId || undefined }),
    staleTime: 30_000,
  });

  const { data: detail, isLoading: detailLoading, error: detailError } = useQuery({
    enabled: !!selectedId,
    queryKey: ['admin-membership', selectedId],
    queryFn: () => fetchAdminMembershipDetail(selectedId || ''),
  });

  React.useEffect(() => {
    if (detail?.membership) {
      setStatusUpdate(detail.membership.status);
    }
  }, [detail?.membership]);

  const { data: slotsData, isLoading: slotsLoading, error: slotsError } = useQuery({
    enabled: !!selectedId,
    queryKey: ['admin-available-slots', selectedId],
    queryFn: () => fetchAvailableSlots(selectedId || '', 30),
  });

  const cancelMutation = useMutation({
    mutationFn: (payload: { reason?: string; payoff_amount_cents?: number; override_no_payment?: boolean }) =>
      cancelMembership(selectedId || '', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-memberships'] });
      queryClient.invalidateQueries({ queryKey: ['admin-membership', selectedId] });
    },
  });

  const bookVisitMutation = useMutation({
    mutationFn: (slot: AvailableSlot & { notes?: string }) =>
      bookVisit(selectedId || '', {
        visit_date: slot.date,
        visit_window: slot.window,
        visit_type: visitType,
        notes: slot.notes,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-membership', selectedId] });
      queryClient.invalidateQueries({ queryKey: ['admin-available-slots', selectedId] });
      setSelectedSlotKey('');
      setVisitNotes('');
    },
  });

  const slotOptions = slotsData?.availableSlots || [];

  const stopAutoRenewMutation = useMutation({
    mutationFn: () => stopAutoRenew(selectedId || ''),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-membership', selectedId] });
      queryClient.invalidateQueries({ queryKey: ['admin-memberships'] });
    },
  });

  const payoffMutation = useMutation({
    mutationFn: () => payoffMembership(selectedId || '', payoffReason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-membership', selectedId] });
      queryClient.invalidateQueries({ queryKey: ['admin-memberships'] });
      setPayoffReason('');
    },
  });

  const statusMutation = useMutation({
    mutationFn: () => updateMembershipStatus(selectedId || '', (statusUpdate as any) || 'active', statusReason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-membership', selectedId] });
      queryClient.invalidateQueries({ queryKey: ['admin-memberships'] });
    },
  });

  const updatePaymentMutation = useMutation({
    mutationFn: async () => {
      if (!selectedId) {
        throw new Error('Select a membership first.');
      }

      const companyRow = selectedMembership?.company_id ? companyById.get(selectedMembership.company_id) : null;
      const brandKey = companyRow && (companyRow as any).brand_key ? String((companyRow as any).brand_key) : null;

      if (!brandKey) {
        throw new Error('Could not resolve company brand_key for Stax tokenization.');
      }

      if (!isStaxReady || cardFormStatus !== 'mounted') {
        throw new Error('Payment system is still loading. Please wait a moment and try again.');
      }

      if (!paymentFormRef.current) {
        throw new Error('Payment form is not ready. Please refresh and try again.');
      }

      // Get customer name from membership for tokenization
      const customerFirstName = selectedMembership?.customer_first_name || '';
      const customerLastName = selectedMembership?.customer_last_name || '';

      const paymentToken = await tokenizePaymentMethod({
        form: paymentFormRef.current,
        extraDetails: {
          firstname: customerFirstName,
          lastname: customerLastName,
        },
        brandKey,
      });

      if (!paymentToken) {
        throw new Error('Failed to tokenize payment method.');
      }

      return updatePaymentMethodAdmin(selectedId, paymentToken);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-membership', selectedId] });
      setCardMonth('');
      setCardYear('');
    },
  });

  const selectedMembership = detail?.membership;
  const transactions = detail?.transactions || [];
  const visits = detail?.visits || [];

  const enrollmentSource =
    selectedMembership?.enrollment_source || (selectedMembership?.metadata?.sale?.source as string | undefined) || null;

  const soldByAgentLogin =
    (selectedMembership?.metadata?.sale?.sold_by_agent_login as string | null | undefined) || null;

  const soldByTechnicianName =
    (selectedMembership?.metadata?.sale?.sold_by_technician_name as string | null | undefined) ||
    (selectedMembership?.metadata?.sale?.sold_by_employee_code as string | null | undefined) ||
    (selectedMembership?.metadata?.sale?.sold_by_technician_id as string | null | undefined) ||
    null;

  const soldByTechnicianEmail =
    (selectedMembership?.metadata?.sale?.sold_by_technician_email as string | null | undefined) || null;

  const soldByAgentDisplay =
    soldByAgentLogin || (enrollmentSource === 'membership_signup_site' ? soldByTechnicianEmail : null);

  const soldByTechnicianDetails =
    soldByTechnicianName && soldByTechnicianEmail
      ? `${soldByTechnicianName} (${soldByTechnicianEmail})`
      : soldByTechnicianName || soldByTechnicianEmail || null;

  const soldViaLabel =
    enrollmentSource === 'contact_center'
      ? 'Contact center'
      : enrollmentSource === 'admin'
      ? 'Admin'
      : enrollmentSource === 'membership_signup_site'
      ? 'Membership signup site'
      : enrollmentSource === 'tech_portal'
      ? 'Technician portal'
      : enrollmentSource || '—';

  return (
    <div className="p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Membership Management</h1>
          <p className="text-sm text-gray-600">Search and inspect internal memberships</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Input
            placeholder="Search name, email, phone, membership #"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full sm:w-72"
          />
          <Select value={status} onChange={(e) => setStatus(e.target.value as AdminMembershipStatus | '')}>
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          <Select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
            <option value="">All companies</option>
            {(companies || []).map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
          <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
            Create membership
          </Button>
          <Button variant="secondary" size="sm" onClick={refreshData} disabled={isFetching}>
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </Button>
        </div>
      </div>

      {isLoading && <p>Loading memberships...</p>}
      {error && <p className="text-red-500">Error: {error.message}</p>}

      <div className="bg-white shadow-md rounded-lg overflow-x-auto">
        <table className="w-full table-fixed divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-[18%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Member</th>
              <th className="w-[12%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
              <th className="w-[10%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Membership #</th>
              <th className="w-[10%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
              <th className="w-[8%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="w-[6%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visits</th>
              <th className="w-[8%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sold Date</th>
              <th className="w-[10%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sold By</th>
              <th className="w-[8%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Next Billing</th>
              <th className="w-[8%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">MRR</th>
              <th className="w-[120px] px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 border-l border-gray-200">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {memberships?.map((membership: AdminMembershipSummary) => {
              const visitsUsed =
                typeof membership.total_visits_allowed === 'number' && typeof membership.visits_remaining === 'number'
                  ? membership.total_visits_allowed - membership.visits_remaining
                  : undefined;

              const isSelected = membership.id === selectedId;

              return (
                <tr
                  key={membership.id}
                  className={`${isSelected ? 'bg-blue-50' : ''} hover:bg-gray-50 cursor-pointer`}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedId(membership.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedId(membership.id);
                    }
                  }}
                >
                  <td className="px-4 py-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {membership.customer_first_name} {membership.customer_last_name}
                      </div>
                      <div className="text-sm text-gray-500 truncate">{membership.customer_email}</div>
                      <div className="text-xs text-gray-400 truncate">{membership.customer_phone}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    <div className="truncate">
                      {(companyById.get(membership.company_id) as any)?.name || membership.company_id}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    <div className="truncate">{membership.membership_number}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    <div className="truncate">{membership.plan_name}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        membership.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : membership.status === 'past_due'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {membership.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {visitsUsed ?? '—'}/{membership.total_visits_allowed ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{formatDate(membership.created_at)}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    <div className="truncate">
                      {membership.sold_by_technician_name || membership.sold_by_agent_login || membership.sale_source || '—'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{formatDate(membership.next_billing_date)}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {formatCents(membership.final_price_cents)} / {membership.billing_frequency === 'monthly' ? 'mo' : 'yr'}
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-medium sticky right-0 bg-white border-l border-gray-200">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedId(membership.id);
                      }}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              );
            })}
            {!memberships?.length && !isLoading && (
              <tr>
                <td className="px-6 py-4 text-sm text-gray-500" colSpan={11}>
                  No memberships found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isFetching && <p className="text-sm text-gray-500 mt-2">Refreshing...</p>}

      {selectedId && (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-1 bg-white shadow rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold">Membership Detail</h2>
              <Button variant="secondary" size="sm" onClick={() => setSelectedId(null)}>
                Close
              </Button>
            </div>
            {selectedMembership && (
              <div className="flex gap-2 mb-3">
                <Button
                  variant="danger"
                  size="sm"
                  disabled={cancelMutation.isPending}
                  onClick={() => {
                    if (!selectedId) return;
                    if (!window.confirm('Cancel this membership?')) return;

                    const rawAmount = window.prompt(
                      'Cancellation payoff amount (USD). Enter 0 to cancel with no charge:',
                      '0'
                    );
                    if (rawAmount === null) return;

                    const sanitized = rawAmount.replace(/[^0-9.]/g, '');
                    const dollars = sanitized ? Number(sanitized) : 0;
                    const payoffAmountCents = Number.isFinite(dollars) ? Math.round(dollars * 100) : 0;

                    const reason = window.prompt('Optional cancellation reason:', '') || undefined;

                    if (payoffAmountCents > 0) {
                      const charge = window.confirm(
                        `Charge $${(payoffAmountCents / 100).toFixed(2)} and cancel membership?`
                      );

                      if (charge) {
                        cancelMutation.mutate({
                          reason,
                          payoff_amount_cents: payoffAmountCents,
                          override_no_payment: false,
                        });
                        return;
                      }

                      const override = window.confirm('Cancel without charging this fee?');
                      if (!override) return;

                      cancelMutation.mutate({
                        reason,
                        payoff_amount_cents: payoffAmountCents,
                        override_no_payment: true,
                      });
                      return;
                    }

                    cancelMutation.mutate({ reason });
                  }}
                >
                  {cancelMutation.isPending ? 'Cancelling...' : 'Cancel membership'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={stopAutoRenewMutation.isPending}
                  onClick={() => stopAutoRenewMutation.mutate()}
                >
                  {stopAutoRenewMutation.isPending ? 'Stopping auto-renew…' : 'Stop auto-renew'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={bookVisitMutation.isPending || slotsLoading}
                  onClick={() => {
                    if (!selectedId) return;
                    if (!selectedSlotKey) {
                      window.alert('Select an available slot first.');
                      return;
                    }
                    const [date, windowKey] = selectedSlotKey.split('|');
                    bookVisitMutation.mutate({ date, window: windowKey, notes: visitNotes } as any);
                  }}
                >
                  {bookVisitMutation.isPending ? 'Booking…' : 'Book visit'}
                </Button>
              </div>
            )}
            {detailLoading && <p>Loading details...</p>}
            {detailError && <p className="text-red-500">Error: {detailError.message}</p>}
            {selectedMembership && (
              <div className="space-y-2 text-sm">
                <div className="font-semibold">
                  {selectedMembership.customer_first_name} {selectedMembership.customer_last_name}
                </div>
                <div className="text-gray-600">{selectedMembership.customer_email}</div>
                <div className="text-gray-600">{selectedMembership.customer_phone}</div>
                <div className="text-gray-600">
                  Company: {(companyById.get(selectedMembership.company_id) as any)?.name || selectedMembership.company_id}
                </div>
                <div className="text-gray-600">
                  Sold via: {soldViaLabel}
                  {enrollmentSource === 'membership_signup_site' && soldByTechnicianDetails
                    ? ` — ${soldByTechnicianDetails}`
                    : ''}
                </div>
                {(enrollmentSource === 'contact_center' || enrollmentSource === 'membership_signup_site') && (
                  <div className="text-gray-600">Sold by agent: {soldByAgentDisplay || '—'}</div>
                )}
                {enrollmentSource === 'tech_portal' && (
                  <div className="text-gray-600">Sold by technician: {soldByTechnicianName || '—'}</div>
                )}
                <div className="text-gray-700">{selectedMembership.plan_name}</div>
                <div className="text-gray-600">Membership #: {selectedMembership.membership_number}</div>
                <div className="text-gray-600">Status: {selectedMembership.status}</div>
                <div className="text-gray-600">Visits: {selectedMembership.visits_used ?? 0}/{selectedMembership.total_visits_allowed ?? 0}</div>
                <div className="text-gray-600">Next billing: {formatDate(selectedMembership.next_billing_date)}</div>
                <div className="text-gray-600">MRR: {formatCents(selectedMembership.final_price_cents)}</div>
                {typeof selectedMembership.deferred_revenue_balance_cents === 'number' && (
                  <div className="mt-2 rounded-md bg-gray-50 p-2">
                    <div className="text-xs font-semibold text-gray-700">Deferred revenue</div>
                    <div className="flex justify-between text-xs text-gray-600">
                      <span>Total collected</span>
                      <span>{formatCents(selectedMembership.deferred_revenue_payments_cents)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-600">
                      <span>
                        Maintenance delivered ({selectedMembership.deferred_revenue_maintenance_visits_completed ?? 0} ×{' '}
                        {formatCents(selectedMembership.deferred_revenue_maintenance_value_cents)})
                      </span>
                      <span>-{formatCents(selectedMembership.deferred_revenue_maintenance_cost_cents)}</span>
                    </div>
                    <div className="flex justify-between text-xs font-semibold">
                      <span>Balance</span>
                      <span
                        className={
                          selectedMembership.deferred_revenue_balance_cents < 0 ? 'text-red-600' : 'text-green-700'
                        }
                      >
                        {formatCents(selectedMembership.deferred_revenue_balance_cents)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
            {selectedMembership && (
              <div className="mt-4 space-y-2 text-sm">
                <h4 className="text-sm font-semibold">Update payment method</h4>
                <form ref={paymentFormRef} className="grid grid-cols-1 gap-2" onSubmit={(e) => e.preventDefault()}>
                  {/* Stax hosted iframe for card number */}
                  <div className="relative">
                    <div
                      id="card-number"
                      className="h-10 px-3 border border-gray-300 rounded-md bg-white flex items-center"
                    />
                    <label className="absolute -top-2 left-2 text-xs text-gray-500 bg-white px-1">Card Number</label>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      name="month"
                      placeholder="MM"
                      value={cardMonth}
                      onChange={(e) => setCardMonth(e.target.value.slice(0, 2))}
                      inputMode="numeric"
                      maxLength={2}
                    />
                    <Input
                      name="year"
                      placeholder="YYYY"
                      value={cardYear}
                      onChange={(e) => setCardYear(e.target.value.slice(0, 4))}
                      inputMode="numeric"
                      maxLength={4}
                    />
                    {/* Stax hosted iframe for CVV */}
                    <div className="relative">
                      <div
                        id="card-cvv"
                        className="h-10 px-3 border border-gray-300 rounded-md bg-white flex items-center"
                      />
                      <label className="absolute -top-2 left-2 text-xs text-gray-500 bg-white px-1">CVV</label>
                    </div>
                  </div>
                  {cardFormStatus === 'loading' && <p className="text-xs text-gray-500">Loading secure fields…</p>}
                  {cardFormStatus === 'error' && staxError && <p className="text-xs text-red-500">{staxError}</p>}
                  {!isStaxReady && <p className="text-xs text-gray-500">Loading payment system…</p>}
                </form>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={cardFormStatus !== 'mounted' || !cardMonth || !cardYear || updatePaymentMutation.isPending}
                  onClick={() => {
                    // Set brand key before tokenizing
                    const companyRow = selectedMembership?.company_id ? companyById.get(selectedMembership.company_id) : null;
                    const brandKey = companyRow && (companyRow as any).brand_key ? String((companyRow as any).brand_key) : null;
                    if (brandKey) setBrandKey(brandKey);
                    updatePaymentMutation.mutate();
                  }}
                >
                  {updatePaymentMutation.isPending ? 'Updating…' : 'Update payment method'}
                </Button>
                {updatePaymentMutation.error && (
                  <p className="text-sm text-red-500">Payment update error: {(updatePaymentMutation.error as Error).message}</p>
                )}
              </div>
            )}
            {slotsLoading && <p className="text-sm text-gray-500 mt-2">Loading available slots…</p>}
            {slotsError && <p className="text-sm text-red-500">Slots error: {slotsError.message}</p>}
            {slotOptions.length > 0 && (
              <div className="mt-4 space-y-2">
                <h4 className="text-sm font-semibold">Book a visit</h4>
                <Select value={selectedSlotKey} onChange={(e) => setSelectedSlotKey(e.target.value)}>
                  <option value="">Select available slot</option>
                  {slotOptions.map((slot) => (
                    <option key={`${slot.date}|${slot.window}`} value={`${slot.date}|${slot.window}`}>
                      {slot.date} • {slot.window}
                    </option>
                  ))}
                </Select>
                <Select value={visitType} onChange={(e) => setVisitType(e.target.value as 'pm' | 'repair' | 'emergency')}>
                  <option value="pm">Preventative maintenance (pm)</option>
                  <option value="repair">Repair</option>
                  <option value="emergency">Emergency</option>
                </Select>
                <Input
                  placeholder="Notes (optional)"
                  value={visitNotes}
                  onChange={(e) => setVisitNotes(e.target.value)}
                />
                {bookVisitMutation.error && (
                  <p className="text-sm text-red-500">Error booking visit: {(bookVisitMutation.error as Error).message}</p>
                )}
              </div>
            )}
            {selectedMembership && (
              <div className="mt-4 space-y-2">
                <h4 className="text-sm font-semibold">Status / payoff</h4>
                <Select
                  value={statusUpdate}
                  onChange={(e) => setStatusUpdate(e.target.value as AdminMembershipStatus)}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label || 'All'}
                    </option>
                  ))}
                </Select>
                <Input
                  placeholder="Status reason (optional)"
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={statusMutation.isPending}
                  onClick={() => statusMutation.mutate()}
                >
                  {statusMutation.isPending ? 'Updating status…' : 'Update status'}
                </Button>
                <Input
                  placeholder="Payoff/cancel reason"
                  value={payoffReason}
                  onChange={(e) => setPayoffReason(e.target.value)}
                />
                <Button
                  variant="danger"
                  size="sm"
                  disabled={payoffMutation.isPending}
                  onClick={() => payoffMutation.mutate()}
                >
                  {payoffMutation.isPending ? 'Processing payoff…' : 'Payoff/Cancel'}
                </Button>
                {(payoffMutation.error || statusMutation.error) && (
                  <p className="text-sm text-red-500">
                    {(payoffMutation.error || statusMutation.error as Error).message}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="bg-white shadow rounded-lg p-4">
            <h3 className="text-md font-semibold mb-2">Recent Transactions</h3>
            {transactions.length === 0 && <p className="text-sm text-gray-500">No transactions</p>}
            <ul className="divide-y divide-gray-200">
              {transactions.map((tx) => (
                <li key={tx.id} className="py-2 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium capitalize">{tx.transaction_type}</span>
                    <span className="text-gray-700">{formatCents(tx.amount_cents)}</span>
                  </div>
                  <div className="text-gray-500">
                    {formatDate(tx.transaction_date)} • {tx.status}
                  </div>
                  {tx.description && <div className="text-gray-500">{tx.description}</div>}
                  {tx.stax_transaction_id && (
                    <div className="text-xs text-gray-500">Stax confirmation #: {tx.stax_transaction_id}</div>
                  )}
                  {tx.invoice_number && <div className="text-xs text-gray-500">Invoice: {tx.invoice_number}</div>}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white shadow rounded-lg p-4">
            <h3 className="text-md font-semibold mb-2">Recent Visits</h3>
            {visits.length === 0 && <p className="text-sm text-gray-500">No visits</p>}
            <ul className="divide-y divide-gray-200">
              {visits.map((visit) => (
                <li key={visit.id} className="py-2 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium capitalize">{visit.visit_type || 'visit'}</span>
                    <span className="text-gray-700">{formatDate(visit.visit_date)}</span>
                  </div>
                  <div className="text-gray-500">{visit.status || 'scheduled'}</div>
                  {visit.visit_window && <div className="text-gray-500">Window: {visit.visit_window}</div>}
                  {visit.successware_job_number && <div className="text-gray-500">Job #: {visit.successware_job_number}</div>}
                  {visit.notes && <div className="text-gray-500">Notes: {visit.notes}</div>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <AdminCreateMembershipModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        companies={(companies || []) as any}
        defaultCompanyId={companyId || undefined}
        onCreated={(membershipId) => {
          setShowCreateModal(false);
          queryClient.invalidateQueries({ queryKey: ['admin-memberships'] });
          setSelectedId(membershipId);
        }}
      />
    </div>
  );
}
